import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, forkJoin, throwError } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';
import { ApiService } from '../api/api.service';
import { UserProfileService } from '../user/user-profile.service';
import { DeliverySummary } from '../message/delivery.service';
import { FAILED_RECEIPT_STATES } from './report-format';
import { ReportPeriodDays, isoDay, periodRange } from './report-period';

const ENGAGEMENT = 'blackkonnectcxpengagement/api';
const COUNT_URL = `${ENGAGEMENT}/engagement-message-reports/count`;

/** A delivery receipt row (EngagementMessageReportsDTO). */
export interface ReceiptRow {
  id?: number | null;
  uuid?: string | null;
  messageUuid?: string | null;
  subject?: string | null;
  content?: string | null;
  campaignUuid?: string | null;
  contactUuid?: string | null;
  mobileNumber?: string | null;
  fullName?: string | null;
  group?: unknown;
  country?: string | null;
  status?: string | null;
  deliveryStatus?: string | null;
  scheduleStatus?: string | null;
  timeStamp?: string | null;
  networkMessageId?: string | null;
  createdAt?: string | null;
}

/** One row of the SMS gateway log (SmsReportData). */
export interface GatewayRow {
  uuid?: string | null;
  messageId?: string | null;
  bulkReferenceId?: string | null;
  messageReference?: string | null;
  submitId?: string | null;
  receiptId?: string | null;
  destination?: string | null;
  sourceAddress?: string | null;
  mobileCountryCode?: string | null;
  mobileCountry?: string | null;
  carrierName?: string | null;
  operatorName?: string | null;
  clientId?: string | null;
  organisationId?: string | null;
  deliveryStatus?: string | null;
  messageContent?: string | null;
  deliveryStatusNarration?: string | null;
  createdDate?: string | null;
  modifiedDate?: string | null;
  deliveryTimestamp?: string | null;
  receiptTimeStamp?: string | null;
  recordStatus?: string | null;
}

/** One real page of the gateway log, with the total for the whole query (engagement /sms/reports/page). */
export interface GatewayPage {
  items: GatewayRow[];
  total: number;
  page: number;
  size: number;
}

/** Outcome counts for the period (engagement /sms/reports/summary). */
export interface GatewaySummary {
  total: number;
  delivered: number;
  failed: number;
  pending: number;
}

/** Outcomes the gateway log can filter on server-side. */
export type GatewayStatusFilter = 'delivered' | 'failed' | 'pending';

function toGatewayPage(body: unknown): GatewayPage {
  const page = body as Partial<GatewayPage> | null;
  if (!page || !Array.isArray(page.items) || typeof page.total !== 'number') {
    throw new Error('Unexpected gateway page');
  }
  return { items: page.items, total: page.total, page: Number(page.page ?? 0), size: Number(page.size ?? page.items.length) };
}

function toGatewaySummary(body: unknown): GatewaySummary {
  const summary = body as Partial<GatewaySummary> | null;
  if (!summary || typeof summary.total !== 'number') throw new Error('Unexpected gateway summary');
  return {
    total: summary.total,
    delivered: Number(summary.delivered ?? 0),
    failed: Number(summary.failed ?? 0),
    pending: Number(summary.pending ?? 0),
  };
}

/** How the dashboard's sent-message overview groups a period. */
export type SentInterval = 'hour' | 'day' | 'week' | 'month';

/** One hour, day, week or month of a {@link SentOverview}. */
export interface SentBucket {
  start: Date;
  total: number;
  delivered: number;
  failed: number;
}

/**
 * SMS sent from the portal (messages and campaigns) in a period, by recipient, with their delivery
 * outcome: totals, and one bucket per interval with empty ones included. Pending is everything
 * without a final receipt yet.
 */
export interface SentOverview {
  from: Date;
  to: Date;
  interval: SentInterval;
  zone: string;
  total: number;
  delivered: number;
  failed: number;
  pending: number;
  buckets: SentBucket[];
}

function toSentOverview(body: unknown): SentOverview {
  const raw = body as Record<string, unknown> | null;
  if (!raw || typeof raw['total'] !== 'number' || !Array.isArray(raw['buckets'])) {
    throw new Error('Unexpected sent overview');
  }
  const count = (value: unknown) => Math.max(0, Number(value) || 0);
  return {
    from: new Date(String(raw['from'])),
    to: new Date(String(raw['to'])),
    interval: String(raw['interval']) as SentInterval,
    zone: String(raw['zone'] ?? ''),
    total: count(raw['total']),
    delivered: count(raw['delivered']),
    failed: count(raw['failed']),
    pending: count(raw['pending']),
    buckets: (raw['buckets'] as Record<string, unknown>[]).map((bucket) => ({
      start: new Date(String(bucket['start'])),
      total: count(bucket['total']),
      delivered: count(bucket['delivered']),
      failed: count(bucket['failed']),
    })),
  };
}

/** The viewer's own time zone, e.g. "Africa/Gaborone": buckets follow the viewer's clock. */
export function viewerZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export interface PagedRows<T> {
  rows: T[];
  total: number;
  totalPages: number;
}

/** Delivery outcomes for a period and the period of the same length before it. */
export interface PeriodOutcome {
  current: DeliverySummary;
  previous: DeliverySummary;
}

export interface FailureQuery {
  days: ReportPeriodDays;
  /** Failed states to include; all of them when empty. */
  states?: readonly string[];
  /** Digits search the number, anything else searches the name. */
  term?: string;
  page: number;
  size: number;
}

/** Rows per request when collecting a whole list for a CSV export. */
const EXPORT_PAGE = 500;
/** Never pull more than this many rows into the browser for one export. */
export const FAILURE_EXPORT_CAP = 10_000;

/**
 * Read-only queries behind the report pages: organisation-wide delivery
 * counts for a period, failed receipts, sent messages and the SMS gateway
 * log. Every call returns its own stream; nothing here writes to the shared
 * subjects other services keep for the messages pages.
 *
 * Organisation-wide receipt queries filter on the receipt's organisation, so
 * receipts that were stored without one are not counted here (per-message
 * reports, which filter by message, still include them).
 */
@Injectable({ providedIn: 'root' })
export class DeliveryReportService {
  private api = inject(ApiService);
  private user = inject(UserProfileService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private get organizationId(): string {
    return this.user.organizationId ?? '';
  }

  /**
   * Recipients messaged, delivered and failed in the period (by receipt
   * creation time), plus the same for the period before it. Six count
   * requests.
   */
  periodOutcome(days: ReportPeriodDays): Observable<PeriodOutcome> {
    if (!this.isBrowser) return EMPTY;
    if (!this.organizationId) return noOrganisation();
    const { start, previousStart } = periodRange(days);
    const org = { 'organisationUuId.equals': this.organizationId };

    return forkJoin({
      current: this.outcome({ ...org, 'createdAt.greaterThanOrEqual': start.toISOString() }),
      previous: this.outcome({
        ...org,
        'createdAt.greaterThanOrEqual': previousStart.toISOString(),
        'createdAt.lessThan': start.toISOString(),
      }),
    });
  }

  /** One page of failed receipts in the period, newest first. */
  failures(query: FailureQuery): Observable<PagedRows<ReceiptRow>> {
    if (!this.isBrowser) return EMPTY;
    if (!this.organizationId) return noOrganisation();
    return this.api
      .get<unknown>(`${ENGAGEMENT}/v1/engagement-message-reports/organization/${this.organizationId}`, {
        ...this.failureCriteria(query),
        sort: 'createdAt,desc',
        page: String(query.page),
        size: String(query.size),
      })
      .pipe(map((body) => toPage<ReceiptRow>(body, query.size)));
  }

  /** Every failed receipt matching the query, up to FAILURE_EXPORT_CAP, for a CSV export. */
  allFailures(query: Omit<FailureQuery, 'page' | 'size'>): Observable<ReceiptRow[]> {
    const step = (page: number) =>
      this.failures({ ...query, page, size: EXPORT_PAGE }).pipe(map((result) => ({ page, ...result })));

    return step(0).pipe(
      expand((result) => {
        const fetched = (result.page + 1) * EXPORT_PAGE;
        const more =
          result.rows.length === EXPORT_PAGE && fetched < FAILURE_EXPORT_CAP && fetched < result.total;
        return more ? step(result.page + 1) : EMPTY;
      }),
      reduce<{ rows: ReceiptRow[] }, ReceiptRow[]>((all, result) => all.concat(result.rows), []),
      map((rows) => rows.slice(0, FAILURE_EXPORT_CAP))
    );
  }

  /**
   * Messages created in the period that have gone out (completed or still
   * sending), newest first. Archived messages are included: archiving only
   * files them away from the messages list.
   */
  sentMessages(days: ReportPeriodDays, page: number, size: number): Observable<PagedRows<any>> {
    if (!this.isBrowser) return EMPTY;
    if (!this.organizationId) return noOrganisation();
    const { start } = periodRange(days);
    return this.api
      .get<unknown>(`${ENGAGEMENT}/v1/organization/${this.organizationId}/engagement-messages`, {
        'status.in': ['Completed', 'Active'],
        'createdAt.greaterThanOrEqual': start.toISOString(),
        sort: 'createdAt,desc',
        page: String(page),
        size: String(size),
        pageSize: String(size),
      })
      .pipe(map((body) => toPage<any>(body, size)));
  }

  /**
   * One page of the SMS gateway log for the period, newest first. The
   * endpoint returns a bare list with no total, so callers can only tell
   * there may be more when a page comes back full.
   */
  gatewayLog(days: ReportPeriodDays, page: number, size: number): Observable<GatewayRow[]> {
    if (!this.isBrowser) return EMPTY;
    if (!this.organizationId) return noOrganisation();
    const { start } = periodRange(days);
    return this.api
      .get<unknown>(`${ENGAGEMENT}/dashboard/messages/${this.organizationId}/sms/reports`, {
        page: String(page),
        size: String(size),
        sort: 'createdDate',
        order: 'desc',
        startDate: isoDay(start),
        endDate: isoDay(new Date()),
        daysBack: String(days),
      })
      .pipe(map((body) => (Array.isArray(body) ? (body as GatewayRow[]) : [])));
  }

  /**
   * One real page of the gateway log for the period, newest first, with the
   * total for every page. The filters run in the SMS gateway's own query, so
   * this covers the whole period. 404s until the engagement service with
   * /sms/reports/page is deployed; callers fall back to gatewayLog().
   */
  gatewayPage(
    days: ReportPeriodDays,
    page: number,
    size: number,
    options: { status?: GatewayStatusFilter | null; search?: string } = {}
  ): Observable<GatewayPage> {
    if (!this.isBrowser) return EMPTY;
    if (!this.organizationId) return noOrganisation();
    const params: Record<string, string> = { ...this.gatewayPeriod(days), page: String(page), size: String(size) };
    if (options.status) params['status'] = options.status;
    const digits = (options.search ?? '').replace(/\D/g, '');
    if (digits) params['search'] = digits;
    return this.api
      .get<unknown>(`${ENGAGEMENT}/dashboard/messages/${this.organizationId}/sms/reports/page`, params)
      .pipe(map(toGatewayPage));
  }

  /** Total, delivered, failed and on-the-way counts for the whole period. */
  gatewaySummary(days: ReportPeriodDays): Observable<GatewaySummary> {
    if (!this.isBrowser) return EMPTY;
    if (!this.organizationId) return noOrganisation();
    return this.api
      .get<unknown>(`${ENGAGEMENT}/dashboard/messages/${this.organizationId}/sms/reports/summary`, this.gatewayPeriod(days))
      .pipe(map(toGatewaySummary));
  }

  /**
   * Recipients of the organisation's messages and campaigns sent from `from` up to `to`, in
   * buckets of `interval` that follow `zone` (the viewer's, by default).
   */
  sentOverview(from: Date, to: Date, interval: SentInterval, zone = viewerZone()): Observable<SentOverview> {
    if (!this.isBrowser) return EMPTY;
    if (!this.organizationId) return noOrganisation();
    return this.api
      .get<unknown>(`${ENGAGEMENT}/dashboard/messages/${this.organizationId}/sent/overview`, {
        from: from.toISOString(),
        to: to.toISOString(),
        interval,
        zone,
      })
      .pipe(map(toSentOverview));
  }

  // ── internals ──

  private gatewayPeriod(days: ReportPeriodDays): Record<string, string> {
    const { start } = periodRange(days);
    return { startDate: isoDay(start), endDate: isoDay(new Date()), daysBack: String(days) };
  }

  private outcome(criteria: Record<string, string>): Observable<DeliverySummary> {
    const count = (states?: readonly string[]) =>
      this.api
        .get<number>(COUNT_URL, { ...criteria, ...(states ? { 'deliveryStatus.in': [...states] } : {}) })
        .pipe(map((value) => Number(value) || 0));

    return forkJoin({
      total: count(),
      delivered: count(['DELIVERED']),
      failed: count(FAILED_RECEIPT_STATES),
    }).pipe(
      map(({ total, delivered, failed }) => ({
        total,
        delivered,
        failed,
        inFlight: Math.max(0, total - delivered - failed),
      }))
    );
  }

  private failureCriteria(query: Omit<FailureQuery, 'page' | 'size'>): Record<string, string | string[]> {
    const { start } = periodRange(query.days);
    const allowed = FAILED_RECEIPT_STATES as readonly string[];
    const states = (query.states ?? []).filter((state) => allowed.includes(state));
    const criteria: Record<string, string | string[]> = {
      'deliveryStatus.in': states.length ? [...states] : [...allowed],
      'createdAt.greaterThanOrEqual': start.toISOString(),
    };

    const term = (query.term ?? '').trim();
    if (term) {
      const digits = term.replace(/[\s()+-]/g, '');
      if (/^\d+$/.test(digits)) criteria['mobileNumber.contains'] = digits;
      else criteria['fullName.contains'] = term;
    }
    return criteria;
  }
}

/** Fails loudly, so pages show their error state rather than loading forever. */
function noOrganisation(): Observable<never> {
  return throwError(() => new Error('This session has no organisation.'));
}

/** Reads a GenericResponse ({items, itemPagination}), a Spring page or a bare list. */
function toPage<T>(body: unknown, size: number): PagedRows<T> {
  const data = body as any;
  const rows: T[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data?.content)
    ? data.content
    : [];
  const totalRaw = Number(data?.itemPagination?.totalElements ?? data?.totalElements);
  const total = Number.isFinite(totalRaw) ? totalRaw : rows.length;
  const pagesRaw = Number(data?.itemPagination?.totalPages ?? data?.totalPages);
  const totalPages = Number.isFinite(pagesRaw) && pagesRaw > 0 ? pagesRaw : Math.max(1, Math.ceil(total / size));
  return { rows, total, totalPages };
}
